import type { components } from "@awtoban/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { ensureProvisioned } from "../../engine/provision";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import type { OperatorFormValues } from "./form-types";

// The wasm result_json is a superset of the server /preview response, so the
// quote stays the same typed shape consumers already use.
type Quote = components["schemas"]["Quote"];

/**
 * Subset of the form that actually moves the quote — used as the query key so
 * cosmetic fields (plate text, owner name) don't trigger a recompute.
 */
function pricingKey(v: OperatorFormValues) {
  return {
    from: v.fromCityId,
    to: v.toCityId,
    mass: v.totalMassTn,
    front: v.frontAxleTn,
    axles: v.totalAxles,
    over: v.isOversized,
  };
}

function isReady(v: OperatorFormValues): boolean {
  return Boolean(
    v.fromCityId &&
      v.toCityId &&
      v.fromCityId !== v.toCityId &&
      v.totalMassTn &&
      v.frontAxleTn &&
      v.totalAxles,
  );
}

/**
 * Live price preview computed LOCALLY by the wasm core (spec §C) — the same
 * binary the server runs, against the cached tariff snapshot + distance matrix.
 * No network: the calculator always works offline once provisioned, and the
 * result is byte-identical to what the server persists at commit (one engine).
 *
 * Return contract preserved for consumers: `{ kind, quote? }` with
 * `kind ∈ { ok, distance_missing, not_provisioned }`.
 */
export function usePreview(values: OperatorFormValues) {
  const debounced = useDebouncedValue(values, 120);
  const ready = isReady(debounced);

  return useQuery({
    queryKey: ["preview", pricingKey(debounced)],
    enabled: ready,
    placeholderData: keepPreviousData,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 30_000,
    queryFn: async () => {
      const prov = await ensureProvisioned();
      if (prov === null) {
        return { kind: "not_provisioned" as const };
      }
      const distance = prov.distanceFor(Number(debounced.fromCityId), Number(debounced.toCityId));
      if (distance === null) {
        return { kind: "distance_missing" as const };
      }
      const inputsJson = JSON.stringify({
        total_mass_tn: debounced.totalMassTn,
        distance_km: distance,
        front_axle_tn: debounced.frontAxleTn,
        total_axles: Number(debounced.totalAxles),
        is_oversized: debounced.isOversized,
      });
      const result = JSON.parse(prov.engine.quote(inputsJson, prov.snapshotJson));
      if (result.kind !== "ok") {
        return { kind: "distance_missing" as const };
      }
      return { kind: "ok" as const, quote: result as Quote };
    },
  });
}
